import React, { useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";

/**
 * The health plan editor's confirmation popup. It replaces the browser's
 * window.confirm, which drew the editor's questions ("Publish Version 21?")
 * as a "localhost:3001 says" alert (Nittin, 2026-09-17: popups, in phr's
 * design language, in phr and bridge's phr).
 *
 * The card is phr's own confirmation (sections/common/DeleteConfirmDialog,
 * Figma 3410:2786): 420w, radius 16, #e2e4e9 border, the two-layer shadow, a
 * 28px bordered ✕, the body copy, and an equal-width Cancel / action pair —
 * with the editor's purple primary as the action instead of the delete red.
 *
 * Behaviour, and why:
 *   · PORTALLED to <body>: the editor's columns are scrollers that would clip
 *     or trap a fixed backdrop.
 *   · Escape, the ✕, Cancel and a press on the backdrop all CANCEL. Escape is
 *     caught in the capture phase so nothing underneath acts on it too.
 *   · Focus goes to the CARD, not a button — a stray Enter must not publish —
 *     stays inside it while it is open, and goes back where it was after.
 *
 * This file is shared by phr and bridge and must stay identical in both.
 */
const HpConfirmDialog = ({ title, body, confirmLabel, onConfirm, onCancel }) => {
  const cardRef = useRef(null);
  // The host re-renders while this is open; a ref keeps the latest handler
  // without re-running the focus effect (which would pull focus to the card).
  const cancelRef = useRef(onCancel);
  cancelRef.current = onCancel;

  useEffect(() => {
    const before = document.activeElement;
    if (cardRef.current) cardRef.current.focus();
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        cancelRef.current();
        return;
      }
      if (e.key !== "Tab" || !cardRef.current) return;
      const buttons = cardRef.current.querySelectorAll("button:not([disabled])");
      if (!buttons.length) return;
      const first = buttons[0];
      const last = buttons[buttons.length - 1];
      const at = document.activeElement;
      if (e.shiftKey && (at === first || at === cardRef.current)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (at === last || !cardRef.current.contains(at))) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      if (before && before.focus && document.contains(before)) before.focus();
    };
  }, []);

  const paragraphs = (Array.isArray(body) ? body : [body]).filter(Boolean);

  return ReactDOM.createPortal(
    <div
      className="hp-confirm-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        className="hp-confirm"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="hp-confirm-title"
        aria-describedby="hp-confirm-body"
        tabIndex={-1}
        ref={cardRef}
      >
        <header className="hp-confirm-head">
          <h2 id="hp-confirm-title" className="hp-confirm-title">
            {title}
          </h2>
          <button type="button" className="hp-confirm-x" aria-label="Close" onClick={onCancel}>
            <svg viewBox="0 0 28 28" fill="none" aria-hidden="true">
              <path d="M7 7L21 21M21 7L7 21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </header>

        <div id="hp-confirm-body" className="hp-confirm-body">
          {paragraphs.map((p) => (
            <p key={p}>{p}</p>
          ))}
        </div>

        <footer className="hp-confirm-actions">
          <button type="button" className="hp-confirm-cancel" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="hp-confirm-ok" onClick={onConfirm}>
            {confirmLabel}
          </button>
        </footer>
      </div>
    </div>,
    document.body
  );
};

HpConfirmDialog.propTypes = {
  title: PropTypes.string.isRequired,
  /** One paragraph, or several (rendered as separate paragraphs). */
  body: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  confirmLabel: PropTypes.string.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default HpConfirmDialog;
